import React from "react";
import { cn } from "utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

export const useTruncated = () => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const checkTruncation = () => {
      const element = ref.current;
      if (element) {
        setIsTruncated(element.scrollWidth > element.clientWidth);
      }
    };

    let timeoutId: NodeJS.Timeout;
    const debouncedCheck = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkTruncation, 100);
    };

    checkTruncation();
    window.addEventListener("resize", debouncedCheck);

    return () => {
      window.removeEventListener("resize", debouncedCheck);
      clearTimeout(timeoutId);
    };
  }, []);

  return { ref, isTruncated };
};

interface TruncatedTextProps {
  text: string | undefined;
  className?: string;
}

export const TruncatedText = ({ text, className }: TruncatedTextProps) => {
  const { ref, isTruncated } = useTruncated();

  if (!text) return null;

  const spanClassName = cn("truncate", className || "block w-full");

  if (isTruncated) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: giving this element a role is a separate accessibility change */}
          <span ref={ref} className={spanClassName} aria-label={text}>
            {text}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xl whitespace-normal">
          {text}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: giving this element a role is a separate accessibility change
    <span ref={ref} className={spanClassName} aria-label={text}>
      {text}
    </span>
  );
};
